import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAddEquipmentAlias, useDeleteEquipmentAlias } from '../../hooks/useEquipment';
import { EQUIPMENT_KIND_LABELS, EQUIPMENT_KINDS } from '../../types';
import type { EquipmentAlias, EquipmentKind, EquipmentModelInput, EquipmentModelRow } from '../../types';
import { NumberField, Section, SelectField, TextField } from '../fields';

const KIND_OPTIONS = EQUIPMENT_KINDS.map((kind) => ({ value: kind, label: EQUIPMENT_KIND_LABELS[kind] }));

interface OverviewTabProps {
  model: EquipmentModelRow;
  aliases: EquipmentAlias[];
  canWrite: boolean;
  onSave: (data: Partial<EquipmentModelInput>) => void;
}

/** Identity, dimensions, aliases and provenance. Every field autosaves on blur/change. */
export function EquipmentOverviewTab({ model, aliases, canWrite, onSave }: OverviewTabProps) {
  const disabled = !canWrite;
  const addAlias = useAddEquipmentAlias();
  const deleteAlias = useDeleteEquipmentAlias();
  const [aliasBrand, setAliasBrand] = useState('');
  const [aliasModel, setAliasModel] = useState('');

  const required = (key: 'brand' | 'model' | 'label') => (value: string) => {
    if (value.trim()) onSave({ [key]: value.trim() });
  };

  return (
    <div className="space-y-5">
      <Section label="Identity">
        <div className="grid grid-cols-2 gap-3">
          <TextField id="eq-brand" label="Brand *" value={model.brand} disabled={disabled} onCommit={required('brand')} />
          <TextField id="eq-model" label="Model *" value={model.model} disabled={disabled} onCommit={required('model')} />
          <TextField id="eq-label" label="Label *" value={model.label} disabled={disabled} onCommit={required('label')} />
          <SelectField<EquipmentKind> id="eq-kind" label="Type" value={model.kind} options={KIND_OPTIONS} disabled={disabled} allowEmpty
            onChange={(kind) => onSave({ kind })} />
        </div>
      </Section>

      <Section label="Dimensions (mm)">
        <div className="grid grid-cols-3 gap-3">
          <NumberField id="eq-w" label="Width" value={model.wMm} min={1} integer disabled={disabled} onCommit={(wMm) => onSave({ wMm })} />
          <NumberField id="eq-d" label="Depth" value={model.dMm} min={1} integer disabled={disabled} onCommit={(dMm) => onSave({ dMm })} />
          <NumberField id="eq-h" label="Height" value={model.hMm} min={1} integer disabled={disabled} onCommit={(hMm) => onSave({ hMm })} />
        </div>
      </Section>

      <Section label="Plate spellings that match this model">
        <div className="flex flex-wrap gap-1.5">
          {aliases.map((alias) => (
            <span key={alias.id} className="inline-flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-xs font-mono">
              {alias.brandNorm} · {alias.modelNorm}
              {canWrite && aliases.length > 1 && (
                <button type="button" aria-label={`Remove alias ${alias.brandNorm} ${alias.modelNorm}`}
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => deleteAlias.mutate({ id: model.id, aliasId: alias.id })}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canWrite && (
          <form className="flex gap-2" onSubmit={(e) => {
            e.preventDefault();
            if (!aliasBrand.trim() || !aliasModel.trim()) return;
            addAlias.mutate({ id: model.id, brand: aliasBrand.trim(), model: aliasModel.trim() }, {
              onSuccess: () => { setAliasBrand(''); setAliasModel(''); },
            });
          }}>
            <Input aria-label="Alias brand" placeholder="Brand as printed" value={aliasBrand} onChange={(e) => setAliasBrand(e.target.value)} className="h-8 text-sm" />
            <Input aria-label="Alias model" placeholder="Model as printed" value={aliasModel} onChange={(e) => setAliasModel(e.target.value)} className="h-8 text-sm" />
            <Button type="submit" variant="outline" size="sm" disabled={addAlias.isPending}><Plus className="h-4 w-4" /></Button>
          </form>
        )}
      </Section>

      <Section label="Source">
        <TextField id="eq-source" label="Where these facts come from" value={model.source} multiline disabled={disabled}
          onCommit={(source) => onSave({ source: source.trim() || null })} />
      </Section>
    </div>
  );
}

/** Create form: local state only, posted by the panel footer's "Create model" button. */
export function CreateModelForm({ value, onChange }: {
  value: { brand: string; model: string; label: string; kind: EquipmentKind | null; labelTouched: boolean };
  onChange: (next: { brand: string; model: string; label: string; kind: EquipmentKind | null; labelTouched: boolean }) => void;
}) {
  const autoLabel = (brand: string, modelName: string) => `${brand.trim()} ${modelName.trim()}`.trim();
  const set = (patch: Partial<typeof value>) => {
    const next = { ...value, ...patch };
    if (!next.labelTouched) next.label = autoLabel(next.brand, next.model);
    onChange(next);
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      {(['brand', 'model'] as const).map((key) => (
        <div key={key} className="space-y-1">
          <label htmlFor={`eq-new-${key}`} className="text-xs font-medium">{key === 'brand' ? 'Brand *' : 'Model *'}</label>
          <Input id={`eq-new-${key}`} value={value[key]} className="h-8 text-sm" autoFocus={key === 'brand'}
            onChange={(e) => set({ [key]: e.target.value })} />
        </div>
      ))}
      <div className="space-y-1">
        <label htmlFor="eq-new-label" className="text-xs font-medium">Label *</label>
        <Input id="eq-new-label" value={value.label} className="h-8 text-sm" onChange={(e) => set({ label: e.target.value, labelTouched: true })} />
      </div>
      <SelectField<EquipmentKind> id="eq-new-kind" label="Type" value={value.kind} options={KIND_OPTIONS} allowEmpty
        onChange={(kind) => set({ kind })} />
    </div>
  );
}
